import { audio } from '../game/AudioManager.js';
import { gameState } from '../game/GameState.js';

class DialogueManager {
  constructor() {
    this.container = null;
    this.nameElem = null;
    this.textElem = null;
    this.avatarElem = null;
    this.avatarWrapper = null;
    this.currentLines = [];
    this.currentIndex = 0;
    this.isTyping = false;
    this.typingTimer = null;
    this.onCompleteCallback = null;
  }

  mount(root) {
    this.container = document.getElementById('dialogue');
    this.nameElem = document.getElementById('dlg-name');
    this.textElem = document.getElementById('dlg-text');
    this.avatarElem = document.getElementById('dlg-avatar');
    this.avatarWrapper = document.getElementById('dlg-avatar-wrapper');

    if (this.container) {
      this.container.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
        this.advance();
      });
      this.container.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    // Tap anywhere on screen to advance dialogue on touchscreens
    window.addEventListener('pointerdown', (e) => {
      if (this.container && !this.container.classList.contains('hidden')) {
        // If clicking inside computer window simulation, joystick zone, or modal buttons, do not advance
        if (e.target && e.target.closest && e.target.closest('#computer-screen, .app-card, button, input, .touch-btn, .touch-joystick-zone, .nintendo-btn, .nintendo-joystick-zone, .touch-border-wing')) return;
        this.advance();
      }
    });

    window.addEventListener('keydown', (e) => {
      if ((e.code === 'Space' || e.code === 'Enter') && this.container && !this.container.classList.contains('hidden')) {
        e.preventDefault();
        this.advance();
      }
    });
  }

  start(lines, onComplete) {
    if (!lines || lines.length === 0) {
      if (onComplete) onComplete();
      return;
    }
    this.currentLines = lines;
    this.currentIndex = 0;
    this.onCompleteCallback = onComplete;

    if (this.container) {
      this.container.classList.remove('hidden');
    }
    const touchRoot = document.getElementById('touch-controls-root');
    if (touchRoot) touchRoot.classList.add('dialogue-active');

    this.showLine();
  }

  getSpeakerProfile(speaker) {
    const chapter = gameState.currentChapter;

    if (speaker.includes('Queen')) {
      return {
        name: 'Queen Labonno',
        portrait: '/assets/characters/queen-emily/portrait.png',
        theme: 'speaker-queen'
      };
    } else if (speaker.includes('Knight')) {
      return {
        name: 'Knight Talha',
        portrait: '/assets/characters/knight-talha/portrait.png',
        theme: 'speaker-knight'
      };
    } else if (speaker.includes('Advisor')) {
      return {
        name: 'Royal Advisor',
        portrait: '/assets/characters/npcs/advisor_portrait.png',
        theme: 'speaker-advisor'
      };
    } else if (speaker.includes('Champion')) {
      return {
        name: 'Council Champion',
        portrait: '/assets/characters/npcs/boss_portrait.png',
        theme: 'speaker-champion'
      };
    } else if (speaker.includes('Emily') || speaker.includes('Labonno')) {
      if (chapter === 1) {
        return {
          name: 'Young Labonno',
          portrait: '/assets/characters/emily-2011/portrait.png',
          theme: 'speaker-kid-emily'
        };
      } else if (chapter >= 3) {
        return {
          name: 'Queen Labonno',
          portrait: '/assets/characters/queen-emily/portrait.png',
          theme: 'speaker-queen'
        };
      } else {
        return {
          name: 'Labonno',
          portrait: '/assets/characters/emily-2016/portrait.png',
          theme: 'speaker-emily'
        };
      }
    } else if (speaker.includes('Talha')) {
      if (chapter === 1) {
        return {
          name: 'Talha',
          portrait: '/assets/characters/talha-2011/portrait.png',
          theme: 'speaker-kid-talha'
        };
      } else if (chapter >= 3) {
        return {
          name: 'Talha',
          portrait: '/assets/characters/knight-talha/portrait.png',
          theme: 'speaker-knight'
        };
      } else {
        return {
          name: 'Talha',
          portrait: '/assets/characters/talha-2016/portrait.png',
          theme: 'speaker-talha'
        };
      }
    } else {
      return {
        name: 'Story Chronicle',
        portrait: null,
        theme: 'speaker-narration'
      };
    }
  }

  showLine() {
    if (this.currentIndex >= this.currentLines.length) {
      this.close();
      return;
    }
    const item = this.currentLines[this.currentIndex];
    const profile = this.getSpeakerProfile(item.speaker);

    if (this.nameElem) {
      this.nameElem.innerText = profile.name;
      this.nameElem.className = `nameplate ${profile.theme}`;
    }

    if (this.avatarElem && this.avatarWrapper) {
      if (profile.portrait) {
        this.avatarElem.src = profile.portrait;
        this.avatarWrapper.classList.remove('hidden');
      } else {
        this.avatarWrapper.classList.add('hidden');
      }
    }

    this.typeText(item.text, item.speaker);
  }

  typeText(fullText, speaker) {
    if (this.typingTimer) clearInterval(this.typingTimer);
    this.isTyping = true;
    if (this.textElem) this.textElem.textContent = '';
    let i = 0;

    this.typingTimer = setInterval(() => {
      if (i < fullText.length) {
        i++;
        if (this.textElem) this.textElem.textContent = fullText.slice(0, i);
        if (i % 2 === 0) {
          audio.playDialogueBlip(speaker);
        }
      } else {
        clearInterval(this.typingTimer);
        this.isTyping = false;
      }
    }, 24);
  }

  advance() {
    if (this.isTyping) {
      clearInterval(this.typingTimer);
      const item = this.currentLines[this.currentIndex];
      if (this.textElem) this.textElem.textContent = item.text;
      this.isTyping = false;
    } else {
      this.currentIndex++;
      this.showLine();
    }
  }

  close() {
    if (this.container) {
      this.container.classList.add('hidden');
    }
    const touchRoot = document.getElementById('touch-controls-root');
    if (touchRoot) touchRoot.classList.remove('dialogue-active');

    if (this.onCompleteCallback) {
      const cb = this.onCompleteCallback;
      this.onCompleteCallback = null;
      cb();
    }
  }
}

export const dialogueManager = new DialogueManager();
